export const DEFAULT_LOT_QTY = 0.05
export const DEFAULT_TARGET_PCT = 1
export const DEFAULT_METRIC = 'spread'

export const METRIC_LABELS = {
  spread: 'PMP Short − Long',
  locked: 'Locked Basis',
}

/**
 * Preço da próxima compra e da próxima venda de `lotQty` que elevam a
 * métrica escolhida em `targetPct`% do valor atual.
 *
 * spread — PMP Short − Long, o mesmo campo pmpSpread do getter directionalEdges.
 * locked — Locked Basis PnL = pairedQty * (pmpShort − pmpLong).
 *
 * Cada lado é um cenário isolado: a compra não assume que a venda executa.
 * As duas métricas exigem long e short no livro. Em zero, um alvo percentual
 * não desloca o preço.
 */
export function resolveMetric(metric) {
  return metric === 'locked' ? 'locked' : DEFAULT_METRIC
}

export function liftByPct(current, targetPct) {
  const value = Number(current) || 0
  const pct = Number(targetPct)
  if (!(pct > 0) || !Number.isFinite(value)) return value
  return value + Math.abs(value) * (pct / 100)
}

export function targetLockedPnl(currentLocked, targetPct) {
  return liftByPct(currentLocked, targetPct)
}

export function lockedBasisPnl({
  longQty = 0,
  longNotional = 0,
  shortQty = 0,
  shortNotional = 0,
} = {}) {
  const lq = Number(longQty) || 0
  const sq = Number(shortQty) || 0
  const paired = Math.min(lq, sq)
  if (!(paired > 0)) return 0
  const pmpLong = lq > 0 ? (Number(longNotional) || 0) / lq : 0
  const pmpShort = sq > 0 ? (Number(shortNotional) || 0) / sq : 0
  return paired * (pmpShort - pmpLong)
}

export function pmpSpread({
  longQty = 0,
  longNotional = 0,
  shortQty = 0,
  shortNotional = 0,
} = {}) {
  const lq = Number(longQty) || 0
  const sq = Number(shortQty) || 0
  if (!(lq > 0) || !(sq > 0)) return null
  const pmpLong = (Number(longNotional) || 0) / lq
  const pmpShort = (Number(shortNotional) || 0) / sq
  return pmpShort - pmpLong
}

function finitePositive(price) {
  return Number.isFinite(price) && price > 0 ? price : null
}

export function buyPriceForTarget(book, qty, targetLocked) {
  const Q = Number(qty)
  const Lq = Number(book?.longQty) || 0
  const Ln = Number(book?.longNotional) || 0
  const Sq = Number(book?.shortQty) || 0
  const Sn = Number(book?.shortNotional) || 0
  const T = Number(targetLocked)
  if (!(Q > 0) || !(Sq > 0) || !Number.isFinite(T)) return null

  const pmpShort = Sn / Sq
  const newLongQty = Lq + Q
  const price = newLongQty <= Sq
    ? (newLongQty * pmpShort - Ln - T) / Q
    : ((pmpShort - T / Sq) * newLongQty - Ln) / Q

  return finitePositive(price)
}

export function sellPriceForTarget(book, qty, targetLocked) {
  const Q = Number(qty)
  const Lq = Number(book?.longQty) || 0
  const Ln = Number(book?.longNotional) || 0
  const Sq = Number(book?.shortQty) || 0
  const Sn = Number(book?.shortNotional) || 0
  const T = Number(targetLocked)
  if (!(Q > 0) || !(Lq > 0) || !Number.isFinite(T)) return null

  const pmpLong = Ln / Lq
  const newShortQty = Sq + Q
  const price = newShortQty <= Lq
    ? (T - Sn + newShortQty * pmpLong) / Q
    : ((pmpLong + T / Lq) * newShortQty - Sn) / Q

  return finitePositive(price)
}

export function buyPriceForSpread(book, qty, targetSpread) {
  const Q = Number(qty)
  const Lq = Number(book?.longQty) || 0
  const Ln = Number(book?.longNotional) || 0
  const Sq = Number(book?.shortQty) || 0
  const Sn = Number(book?.shortNotional) || 0
  const T = Number(targetSpread)
  if (!(Q > 0) || !(Lq > 0) || !(Sq > 0) || !Number.isFinite(T)) return null

  const pmpShort = Sn / Sq
  return finitePositive(((pmpShort - T) * (Lq + Q) - Ln) / Q)
}

export function sellPriceForSpread(book, qty, targetSpread) {
  const Q = Number(qty)
  const Lq = Number(book?.longQty) || 0
  const Ln = Number(book?.longNotional) || 0
  const Sq = Number(book?.shortQty) || 0
  const Sn = Number(book?.shortNotional) || 0
  const T = Number(targetSpread)
  if (!(Q > 0) || !(Lq > 0) || !(Sq > 0) || !Number.isFinite(T)) return null

  const pmpLong = Ln / Lq
  return finitePositive(((pmpLong + T) * (Sq + Q) - Sn) / Q)
}

export function computeBasisTargets({
  longQty = 0,
  longNotional = 0,
  shortQty = 0,
  shortNotional = 0,
  lotQty = DEFAULT_LOT_QTY,
  targetPct = DEFAULT_TARGET_PCT,
  metric = DEFAULT_METRIC,
} = {}) {
  const book = {
    longQty: Number(longQty) || 0,
    longNotional: Number(longNotional) || 0,
    shortQty: Number(shortQty) || 0,
    shortNotional: Number(shortNotional) || 0,
  }
  const qty = Number(lotQty)
  const pct = Number(targetPct)
  const activeMetric = resolveMetric(metric)
  const currentLocked = lockedBasisPnl(book)
  const currentSpread = pmpSpread(book)
  const currentValue = activeMetric === 'locked' ? currentLocked : currentSpread
  const solvable = qty > 0 && pct > 0 && currentValue != null && Math.abs(currentValue) > 1e-9
  const targetValue = solvable ? liftByPct(currentValue, pct) : currentValue
  const priceFor = activeMetric === 'locked'
    ? { buy: buyPriceForTarget, sell: sellPriceForTarget }
    : { buy: buyPriceForSpread, sell: sellPriceForSpread }

  return {
    metric: activeMetric,
    metricLabel: METRIC_LABELS[activeMetric],
    lotQty: qty > 0 ? qty : 0,
    targetPct: pct > 0 ? pct : 0,
    currentValue,
    targetValue,
    improvement: solvable ? targetValue - currentValue : 0,
    currentLocked,
    targetLocked: Math.abs(currentLocked) > 1e-9 ? liftByPct(currentLocked, pct) : currentLocked,
    currentSpread,
    targetSpread: currentSpread != null && Math.abs(currentSpread) > 1e-9
      ? liftByPct(currentSpread, pct)
      : currentSpread,
    pmpLong: book.longQty > 0 ? book.longNotional / book.longQty : null,
    pmpShort: book.shortQty > 0 ? book.shortNotional / book.shortQty : null,
    buyPrice: solvable ? priceFor.buy(book, qty, targetValue) : null,
    sellPrice: solvable ? priceFor.sell(book, qty, targetValue) : null,
  }
}
